/**
 * Route copy for `src/routes/settings` (keys: `routes.settings.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  aIRegistryExtras: {
    saving: 'Saving...',
    noConfigTheSecretIsThe: '(no config — the secret is the token)',
    toolAuthName: 'Tool auth — {{name}}',
    invalidJsonConfig: 'Invalid JSON config',
    failed: 'Failed',
    theSecretApiKeyOauthSecret: 'The secret (API key, OAuth secret, password) is encrypted at rest and never sent back',
    calledOnBaseUrl: ' — called on {{base_url}}',
    authType: 'Auth type',
    secretNotRequired: 'Secret (not required)',
    secret: 'Secret *',
    authConfigJson: 'Auth config (JSON)',
    saveSecret: 'Save secret',
    cancel: 'Cancel',
    field: '+ Field',
    noFieldsDefined: 'No fields defined.',
    fieldName: 'field_name',
    fieldName2: 'Field name',
    fieldType: 'Field type',
    required: 'required',
    removeThisField: 'Remove this field',
    createASkill: 'Create a skill',
    theSkillNameIsRequired: 'The skill name is required.',
    thePromptContentIsRequired: 'The prompt content is required.',
    chooseAnExistingPrompt: 'Choose an existing prompt.',
    skillNameCreated: 'Skill “{{name}}” created.',
    couldNotCreateTheSkill: 'Could not create the skill.',
    aPromptDrivenSkillNoCoding: 'A prompt-driven skill: no coding required. It will be available in your scope (firm) and can be linked to your agents.',
    skillName: 'Skill name *',
    description: 'Description',
    category: 'Category',
    eGContentCrmAnalyticsCopilot: 'e.g. Content, CRM, Analytics, Copilot',
    implementationPromptBased: 'Implementation — prompt-based',
    writeANewPrompt: 'Write a new prompt',
    useAnExistingPrompt: 'Use an existing prompt',
    noPromptVisibleYetWriteA: 'No prompt visible yet — write a new one.',
    prompt: 'Prompt *',
    choose: '-- choose --',
    promptName: 'Prompt name',
    defaultSkillNamePrompt: 'Default: “<skill name> — prompt”',
    vPrompt: '{{v}} — prompt',
    promptContent: 'Prompt content *',
    useVariableForDynamicPlaceholders: 'Use {variable} for dynamic placeholders',
    variablesCommaSeparated: 'Variables (comma-separated)',
    topicTone: 'topic, tone',
    taskType: 'Task type',
    language: 'Language',
    inputSchema: 'Input schema',
    theFieldsTheAgentMustProvide: 'The fields the agent must provide to use this skill.',
    outputSchema: 'Output schema',
    whatTheSkillReturns: 'What the skill returns.',
    creating: 'Creating…',
    createSkill: 'Create skill',
    skill: 'Skill',
    prompt2: 'Prompt',
    tool: 'Tool',
    capabilityLinkedToTheAgent: 'Capability linked to the agent.',
    couldNotLink: 'Could not link.',
    couldNotRemove: 'Could not remove.',
    effectiveCapabilitiesForYourScope: 'Effective capabilities (for your scope)',
    defaultOnThisAgentManagedBy: 'Default on this agent — managed by the administrator',
    default: 'Default',
    added: 'Added',
    yourLinksAdditionsInYourScope: 'Your links (additions in your scope)',
    noLinksOfYourOwn: 'No links of your own.',
    outsideYourScopeManagedElsewhere: 'Outside your scope — managed elsewhere',
    type: 'Type',
    capability: 'Capability',
    link: '+ Link',
    yourScopeDoesNotAllowLinking: 'Your scope does not allow linking a {{capType}}.',
    none: '(none)',
    mySkill: 'My skill',
  },
  aIRegistryPage: {
    agentsPersonas: 'Agents & personas',
    name: 'Name',
    type: 'Type',
    description: 'Description',
    systemPrompt: 'System prompt',
    defaultModelId: 'Default model (id)',
    skillsIdsCommaSeparated: 'Skills (ids, comma-separated)',
    toolsIdsCommaSeparated: 'Tools (ids, comma-separated)',
    configJson: 'Config (JSON)',
    active: 'Active',
    models: 'Models',
    model: 'model',
    provider: 'Provider',
    modelId: 'Model ID',
    displayName: 'Display name',
    temperature: 'Temperature',
    maxTokens: 'Max tokens',
    inputPrice1kUsd: 'Input price / 1k (USD)',
    outputPrice1kUsd: 'Output price / 1k (USD)',
    capabilitiesCsv: 'Capabilities (csv)',
    eGToolsVision: 'e.g. tools, vision',
    defaultForCsv: 'Default for (csv)',
    eGGenerationCritiqueVision: 'e.g. generation, critique, vision',
    prompts: 'Prompts',
    version: 'Version',
    template: 'Template',
    variablesCsv: 'Variables (csv)',
    taskType: 'Task type',
    language: 'Language',
    skills: 'Skills',
    category: 'Category',
    implementationReference: 'Implementation reference',
    srcSkillsXRunOrPrompt: 'src.skills.x:run or prompt:{id}',
    inputSchemaJson: 'Input schema (JSON)',
    outputSchemaJson: 'Output schema (JSON)',
    tools: 'Tools',
    mcpServer: 'MCP server',
    baseUrlExternal: 'Base URL (external)',
    eGHttpsApiExampleCom: 'e.g. https://api.example.com — defines an external tool (secret via 🔑 Secret)',
    targetService: 'Target service',
    targetEndpoint: 'Target endpoint',
    scopesCsv: 'Scopes (csv)',
    riskLevel: 'Risk level',
    providerInInputPricePer1k: '{{provider}} · in {{input_price_per_1k}}/1k · out {{output_price_per_1k}}/1k',
    key: '••• key',
    defaultV: 'default: {{v}}',
    session: 'session',
    invalidJsonForLabel: 'Invalid JSON for “{{label}}”',
    titleDuplicatedIntoYourScope: '“{{title}}” duplicated into your scope.',
    details: 'Details',
    key2: '🔑 Key',
    secret: '🔑 Secret',
    edit: 'Edit',
    duplicateTitle: 'Duplicate {{title}}',
    createAnEditableCopyInYour: 'Create an editable copy in your scope — available to every role',
    readOnlyCannotDuplicate: 'Read-only — your role cannot duplicate.',
    duplicating: 'Duplicating…',
    duplicate: 'Duplicate',
    deleteTitle: 'Delete {{title}}',
    inactive: 'inactive',
    labels: 'Labels',
    registryEntities: 'Registry entities',
    total: 'Total',
    active2: 'Active',
    inactive2: 'Inactive',
    results: 'Results',
    searchForASingular: 'Search for a {{singular}}…',
    searchTheLabel: 'Search the {{label}}',
    view: 'View',
    cards: 'Cards',
    table: 'Table',
    promptDrivenSkillNoCodingRequired: 'Prompt-driven skill — no coding required',
    yourRoleCannotCreateASkill: 'Your role cannot create a skill (prompts only) — you can still duplicate a skill visible in your scope.',
    createASkill: '+ Create a skill',
    advancedEditingCodeReferenceRawJson: 'Advanced editing: code reference, raw JSON',
    newAdvanced: '+ New (advanced)',
    new: '+ New',
    noResults: 'No results',
    noSingular: 'No {{singular}}',
    refineYourSearch: 'Refine your search.',
    addAnEntryToTheRegistry: 'Add an entry to the registry.',
    delete: 'Delete?',
    delete2: 'Delete',
    doYouReallyWantToDelete: 'Do you really want to delete “',
    thisActionCannotBeUndone: '”? This action cannot be undone.',
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    new2: 'New',
    invalidData: 'Invalid data',
    saving: 'Saving…',
    save: 'Save',
    cancel: 'Cancel',
    byokApiKeyV: 'BYOK API key — {{v}}',
    aKeyIsAlreadySavedMasked: 'A key is already saved (masked). Entering a new value will replace it.',
    theKeyIsEncryptedAtRest: 'The key is encrypted at rest on the server and is never sent back.',
    tenantTenantId: 'Tenant (tenant_id) *',
    apiKey: 'API key *',
    sk: 'sk-…',
    saveKey: 'Save key',
    platform: 'platform',
  },
  byokKeysPage: {
    errorStatus: 'Error {{status}}',
    accessDeniedThisActionRequiresA: 'Access denied — this action requires a role with write permissions.',
    llmKeysByok: 'LLM keys (BYOK)',
    couldNotLoadTheKeys: 'Could not load the keys.',
    enterTheProviderSApiKey: 'Enter the provider\'s API key.',
    keySaved: 'Key saved.',
    keyUpdated: 'Key updated.',
    savingTheKeyFailed: 'Saving the key failed.',
    deleteThisKey: 'Delete this key?',
    generationWillGoBackThroughThe: 'Generation will go back through the shared platform key. This takes effect immediately.',
    delete: 'Delete',
    keyDeleted: 'Key deleted.',
    deletionFailed: 'Deletion failed.',
    useYourOwnProviderKeyOpenai: 'Use your own provider key (OpenAI / Anthropic / Google / Ollama) — generation is then billed to your provider account, not to the shared platform key.',
    addReplaceAKey: 'Add / replace a key',
    theKeyIsEncryptedAtRest: 'The key is encrypted at rest and never shown again: only a masked fingerprint (last 4 characters) appears in the list below.',
    provider: 'Provider',
    apiKey: 'API key',
    writeOnlyNeverShownAgain: 'Write-only — never shown again',
    sk: 'sk-…',
    modelOptional: 'Model (optional)',
    emptyWholeProvider: 'Empty = whole provider',
    gpt4oClaudeSonnet45: 'gpt-4o, claude-sonnet-4-5…',
    labelOptional: 'Label (optional)',
    myProductionKey: 'My production key',
    saving: 'Saving…',
    saveKey: 'Save key',
    savedKeys: 'Saved keys',
    noSavedKeyGenerationUsesThe: 'No saved key. Generation uses the shared platform key.',
    model: 'Model',
    label: 'Label',
    keyMasked: 'Key (masked)',
    updated: 'Updated',
    actions: 'Actions',
    all: 'All',
  },
  caseStudiesSettingsPage: {
    french: 'French',
  },
  chatAppsPage: {
    french: 'French',
  },
  connectionsSettingsPage: {
    professionalPagesPosts: 'Professional pages & posts',
    xTwitter: 'X (Twitter)',
    realTimePublishingAndListening: 'Real-time publishing and listening',
    feedReelsAndStories: 'Feed, reels and stories',
    pagesAndAudiences: 'Pages and audiences',
    videosAndShorts: 'Videos and Shorts',
    shortVideos: 'Short videos',
    active: 'Active',
    expiringSoon: 'Expiring soon',
    expired: 'Expired',
    revoked: 'Revoked',
    reconnectionRequired: 'Reconnection required',
    notConnected: 'Not connected',
    expired2: 'expired',
    revoked2: 'revoked',
    reconnectionRequired2: 'reconnection required',
    noExpiry: 'No expiry',
    expiredOnV: 'Expired on {{v}}',
    expiresInDaysDV: 'Expires in {{days}} d ({{v}})',
    socialConnections: 'Social connections',
    connected: 'Connected',
    active2: 'Active',
    toRenew: 'To renew',
    available: 'Available',
    loadingConnections: 'Loading connections…',
    redirecting: 'Redirecting…',
    connect: 'Connect',
    refresh: 'Refresh',
    disconnectLabel: 'Disconnect {{label}}',
    disconnect: 'Disconnect',
    account: 'Account',
    status: 'Status',
    token: 'Token',
    connectedOn: 'Connected on',
    authorizeOauthAccessToPublishAnd: 'Authorize OAuth access to publish and listen on {{label}}.',
    accessTokensAreEncryptedAndKept: 'Access tokens are encrypted and kept on the server — they never pass through the browser. Reconnecting restarts the provider\'s OAuth flow.',
    disconnectTheProvider: 'Disconnect the provider?',
    theConnectionTo: 'The connection to',
    vWillBeRevokedYouWill: '(@{{v}}) will be revoked. You will need to reconnect it to publish again.',
    active3: 'active',
  },
  connectorConfigPage: {
    notEntered: '— not entered —',
    provided: 'provided',
    noneDash: '— none —',
    apiKey: 'API key',
    aKeySentInAnHttp: 'A key sent in an HTTP header.',
    headerXApiKeyPrefix: '{ "header": "X-API-Key", "prefix": "" }',
    bearerToken: 'Bearer token',
    aTokenSentViaTheAuthorization: 'A token sent via the Authorization: Bearer header.',
    optionalTheSecretIsTheToken: '(optional — the secret is the token)',
    basicUsernamePassword: 'Basic (username / password)',
    httpBasicUsernameAndPassword: 'HTTP Basic username and password.',
    usernameUserTheSecretIsThe: '{ "username": "user" }  (the secret is the password)',
    clientCredentialsSecretOauthConfiguratio: 'Client credentials — secret + OAuth configuration.',
    tokenUrlHttpsTokenClientId: '{ "token_url": "https://.../token", "client_id": "…", "scope": "read" }',
    couldNotLoadTheConnectors: 'Could not load the connectors.',
    keyDeletedForName: 'Key deleted for “{{name}}”.',
    couldNotDelete: 'Could not delete.',
    connectors: 'Connectors',
    configureYourOwnCredentialsApiKey: 'Configure your own credentials (API key, token, OAuth…) for each MCP server, then test the connection.',
    refresh: 'Refresh',
    addAConnector: 'Add a connector',
    loadingConnectors: 'Loading connectors…',
    noConnectors: 'No connectors',
    noConnectorIsAvailableForYour: 'No connector is available for your account yet.',
    deleteTheKey: 'Delete the key?',
    delete: 'Delete',
    doYouReallyWantToDelete: 'Do you really want to delete the credential stored for “',
    theConnectorWillStopWorkingUntil: '”? The connector will stop working until a new key is configured.',
    testFailedForName: 'Test failed for “{{name}}”.',
    couldNotRunTheTest: 'Could not run the test.',
    credentialConfigured: 'Credential configured',
    notConfigured: 'Not configured',
    thisConnectorCanBeUsedBy: 'This connector can be used by agents, under supervision (HITL).',
    availableToAgents: 'Available to agents',
    connectionSucceeded: 'Connection succeeded',
    connectionFailed: 'Connection failed',
    configureACredentialFirstToBe: 'Configure a credential first to be able to test.',
    testing: 'Testing…',
    test: 'Test',
    deleteTheKeyForName: 'Delete the key for {{name}}',
    deleteKey: 'Delete key',
    edit: 'Edit',
    configure: 'Configure',
    authenticationType: 'Authentication type',
    invalidJsonConfiguration: 'Invalid JSON configuration.',
    theSecretIsRequired: 'The secret is required.',
    keySavedYouCanNowTest: 'Key saved. You can now test it.',
    couldNotSave: 'Could not save.',
    configureName: 'Configure — {{name}}',
    chooseTheConnectorSAuthenticationType: 'Choose the connector\'s authentication type.',
    secret: 'Secret *',
    encryptedAtRestOnTheServer: 'Encrypted at rest on the server — never shown again.',
    apiKeyTokenOauthSecretOr: 'API key, token, OAuth secret or password…',
    authConfigurationJsonOptional: 'Auth configuration (JSON, optional)',
    aKeyIsAlreadySavedEntering: 'A key is already saved. Entering a new secret will replace it.',
    connector: 'Connector',
    mcpServer: 'MCP server',
    baseUrl: 'Base URL',
    authType: 'Auth type',
    secret2: 'Secret',
    jsonConfig: 'JSON config',
    testInProgress: 'Test in progress…',
    testTheConnection: 'Test the connection',
    saveAKeyFirstToBe: 'Save a key first to be able to test it.',
    configurationSteps: 'Configuration steps',
    cancel: 'Cancel',
    previous: 'Previous',
    next: 'Next',
    saving: 'Saving…',
    saveKey: 'Save key',
    secretConfiguration: 'Secret & configuration',
    saveTest: 'Save & test',
    hidden: '•••••••• (hidden)',
  },
  connectorForm: {
    apiKey: 'API key',
    aKeySentInAnHttp: 'A key sent in an HTTP header.',
    headerXApiKeyPrefix: '{ "header": "X-API-Key", "prefix": "" }',
    bearerToken: 'Bearer token',
    aTokenSentViaTheAuthorization: 'A token sent via the Authorization: Bearer header.',
    optionalTheSecretIsTheToken: '(optional — the secret is the token)',
    basicUsernamePassword: 'Basic (username / password)',
    httpBasicUsernameAndPassword: 'HTTP Basic username and password.',
    usernameUserTheSecretIsThe: '{ "username": "user" }  (the secret is the password)',
    clientCredentialsSecretOauthConfiguratio: 'Client credentials — secret + OAuth configuration.',
    tokenUrlHttpsTokenClientId: '{ "token_url": "https://.../token", "client_id": "…", "scope": "read" }',
    signInForm: 'Sign-in (form)',
    usernameAndPasswordSubmittedToAn: 'Username and password submitted to an application sign-in form (not a standard HTTP header).',
    loginUrlHttpsLoginUsernameThe: '{ "login_url": "https://.../login", "username": "…" }  (the secret is the password)',
    webhookTriggerUrl: 'Webhook (trigger URL)',
    theConnectorPostsThePayloadTo: 'The connector posts the payload to a signed URL (Azure Logic Apps, Zapier, Make, Slack webhook…). The credential IS that URL.',
    pasteTheFullHttpTriggerUrl: 'Paste the full HTTP trigger URL, signature included (…&sig=…), as the credential.',
    couldNotLoadTheConnectorTemplates: 'Could not load the connector templates.',
    theNameAndBaseUrlAre: 'The name and base URL are required.',
    connectorNameCreatedNowAddIts: 'Connector “{{name}}” created. Now add its credential.',
    couldNotCreateTheConnector: 'Could not create the connector.',
    invalidJsonConfiguration: 'Invalid JSON configuration.',
    theSecretIsRequired: 'The secret is required.',
    credentialSavedYouCanNowTest: 'Credential saved. You can now test it.',
    couldNotSaveTheCredential: 'Could not save the credential.',
    couldNotRunTheTest: 'Could not run the test.',
    addAConnector: 'Add a connector',
    connectorOrigin: 'Connector origin',
    fromATemplate: 'From a template',
    custom: 'Custom',
    loadingTemplates: 'Loading templates…',
    noTemplateAvailableOnThisEnvironment: 'No template available on this environment — use “Custom”.',
    connectorTemplate: 'Connector template *',
    chooseATemplate: 'Choose a template…',
    name: 'Name *',
    connectorDisplayName: 'Connector display name',
    baseUrl: 'Base URL *',
    httpsApiExampleCom: 'https://api.example.com',
    authenticationType: 'Authentication type *',
    descriptionOptional: 'Description (optional)',
    whatIsThisConnectorFor: 'What is this connector for?',
    credentialHint: 'Credential hint:',
    connector: 'Connector “',
    createdEnterItsCredentialToMake: '” created. Enter its credential to make it usable by agents.',
    secret: 'Secret *',
    encryptedAtRestOnTheServer: 'Encrypted at rest on the server — never shown again.',
    apiKeyTokenOauthSecretOr: 'API key, token, OAuth secret or password…',
    authConfigurationJsonOptional: 'Auth configuration (JSON, optional)',
    testTheConnectionFor: 'Test the connection for “',
    testInProgress: 'Test in progress…',
    testTheConnection: 'Test the connection',
    connectionSucceeded: 'Connection succeeded',
    connectionFailed: 'Connection failed',
    theConnectorIsNowAvailableTo: 'The connector is now available to agents, under supervision (HITL).',
    cancel: 'Cancel',
    creating: 'Creating…',
    createConnector: 'Create connector',
    configureLater: 'Configure later',
    saving: 'Saving…',
    saveCredential: 'Save credential',
    done: 'Done',
    connectorSetupSteps: 'Connector setup steps',
    connector2: 'Connector',
    identifier: 'Identifier',
    test: 'Test',
  },
  creditsSettingsPage: {
    reservation: 'Reservation',
    settlement: 'Settlement',
    release: 'Release',
    date: 'Date',
    service: 'Service',
    function: 'Function',
    model: 'Model',
    tokensInOut: 'Tokens (in/out)',
    credits: 'Credits',
    cost: 'Cost',
    thisActionHasAlreadyBeenRetried: 'This action has already been retried.',
    yourActionWasRetriedSuccessfully: 'Your action was retried successfully.',
    type: 'Type',
    amount: 'Amount',
    cr: 'cr',
    balanceAfter: 'Balance after',
    source: 'Source',
    note: 'Note',
    ledgerFeature: 'Feature',
    ledgerActor: 'By',
    ledgerEntity: 'Item',
    close: 'Close',
    yourBalanceHasBeenToppedUp: 'Your balance has been topped up. Resume the interrupted action?',
    dismiss: 'Dismiss',
    resuming: 'Resuming…',
    topUpAndContinue: 'Top up and continue',
    wallet: 'Wallet',
    perMemberBudgets: 'Per-member budgets',
    buyCredits: 'Buy credits',
    loadingWallet: 'Loading wallet…',
    unlimitedCredits: 'unlimited credits',
    creditsAvailable: 'credits available',
    unlimitedCreditsSuperadmin: 'Unlimited credits (superadmin)',
    lowBalanceConsiderToppingUp: 'Low balance — consider topping up',
    healthyBalance: 'Healthy balance',
    totalBalance: 'Total balance',
    available: 'Available',
    reserved: 'Reserved',
    alertThreshold: 'Alert threshold',
    reservedTotalBalance: 'Reserved / total balance',
    vV2Cr: '{{v}} / {{v2}} cr',
    myMonthlyCapAdmin: 'My monthly cap (admin)',
    walletUnavailable: 'Wallet unavailable',
    theCreditsServiceDidNotRespond: 'The credits service did not respond. Try again later.',
    creditPurchasesAndAutomaticTopUp: ': credit purchases and automatic top-up.',
    usageLastTrendDaysDays: 'Usage (last {{TREND_DAYS}} days)',
    noUsage: 'No usage',
    billedAiCallsWillAppearHere: 'Billed AI calls will appear here.',
    calls: 'Calls',
    avgActiveDay: 'Avg. / active day',
    creditsUsedPerDay: 'Credits used per day',
    trendDaysDAgo: '{{TREND_DAYS}} d ago',
    today: 'today',
    byService: 'By service',
    byTask: 'By task',
    byModel: 'By model',
    perRequestDetail: 'Per-request detail',
    n100MostRecentRequests: '100 most recent requests',
    noAiRequests: 'No AI requests',
    everyBilledAiCallWillAppear: 'Every billed AI call will appear here, with its service, model, tokens and cost.',
    transactionHistory: 'Transaction history',
    noTransactions: 'No transactions',
    loadMore: 'Load more',
    ledgerShown: '{{n}} transactions shown',
    creditMovementsWillAppearHere: 'Credit movements will appear here.',
    key: 'Key',
    rowDaysDTrend: '{{ROW_DAYS}}d trend',
    share: 'Share',
    automaticTopUpSaved: 'Automatic top-up saved.',
    couldNotStartThePaymentSetup: 'Could not start the payment setup.',
    paymentUnavailableStripeIsNotConfigured: 'Payment unavailable: Stripe is not configured yet.',
    automaticTopUp: 'Automatic top-up',
    buyRenewNow: 'Buy / Renew now',
    automaticallyTopUpYourWalletWhen: 'Automatically top up your wallet when the available balance drops below a threshold — without interrupting your AI actions. A saved card is charged off-session for the chosen pack.',
    onlinePaymentIsNotEnabledOn: 'Online payment is not enabled on this environment — automatic top-up requires Stripe.',
    enableAutomaticTopUp: 'Enable automatic top-up',
    thresholdAvailableCredits: 'Threshold (available credits)',
    packToTopUp: 'Pack to top up',
    vCrV2: '— {{v}} cr ({{v2}})',
    cardSaved: 'Card saved ✓',
    noSavedCard: 'No saved card',
    redirecting: 'Redirecting…',
    changeCard: 'Change card',
    saveACard: 'Save a card',
    saving: 'Saving…',
    save: 'Save',
    saveACardSoTheAutomatic: 'Save a card so the automatic top-up can run.',
    invalidPaymentSession: 'Invalid payment session.',
    buyAiCredits: 'Buy AI credits',
    loadingOffers: 'Loading offers…',
    onlinePaymentIsNotEnabledOn2: 'Online payment is not enabled on this environment yet. Contact an administrator for a manual top-up.',
    popular: 'Popular',
    buy: 'Buy',
    currencyNotConfiguredForThisPack: 'Currency not configured for this pack — price in Canadian dollars.',
    billedInCurrency: '≈ billed in {{currency}}',
    credits2: 'credits',
    v1000Cr: '≈ {{v}} / 1,000 cr',
    securePaymentViaStripe1000: 'Secure payment via Stripe. 1,000 credits ≈ USD 10 of model usage. Credits are added once the payment is confirmed.',
    noOffersAvailable: 'No offers available',
    topUp: 'Top-up',
    unlimited: 'Unlimited',
    notApplicableUnlimited: 'not applicable (unlimited)',
    aiRequests: 'AI requests',
  },
  delegationsPage: {
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    noPermissions: 'No permissions.',
    allPlatforms: 'All platforms',
    allowed: 'allowed',
    denied: 'denied',
    labelAccess: '{{label}} access',
    allow: 'Allow',
    deny: 'Deny',
    enterTheManagerSEmailAddress: 'Enter the manager\'s email address.',
    grantAtLeastOnePermission: 'Grant at least one permission.',
    delegationCreatedAndSent: 'Delegation created and sent.',
    managerEmail: 'Manager email *',
    managerExampleCom: 'manager@example.com',
    label: 'Label',
    optionalEGMartinAgency: 'Optional — e.g. “Martin Agency”',
    displayNameForThisDelegation: 'Display name for this delegation',
    expiry: 'Expiry',
    optionalAccessIsAutomaticallyRevokedOn: 'Optional — access is automatically revoked on this date.',
    grantedPermissions: 'Granted permissions',
    specificPlatforms: 'Specific platforms',
    youAreGrantingAPerson: 'You are granting a person',
    outsideYourOrganization: 'outside your organization',
    access: 'access',
    strictlyLimited: 'strictly limited',
    toThePermissionsAboveInYour: 'to the permissions above, in your account. This person keeps their own account and only sees what you grant. Every action is logged and access is',
    revocableAtAnyTime: 'revocable at any time',
    sending: 'Sending…',
    grantDelegation: 'Grant delegation',
    permission: 'permission',
    selected: 'selected',
    pending: 'Pending',
    revoked: 'Revoked',
    noExpiry: 'No expiry',
    expiresOnV: 'Expires on {{v}}',
    delegationRevoked: 'Delegation revoked.',
    revoke: 'Revoke',
    revokeDelegation: 'Revoke delegation',
    revokeAccessFor: 'Revoke access for “',
    accessIsCutImmediately: '”? Access is cut immediately.',
    delegationAcceptedTheAccountIsAvailable: 'Delegation accepted. The account is available under “Managed accounts”.',
    delegationRemoved: 'Delegation removed.',
    delegatedAccount: 'Delegated account',
    accepting: 'Accepting…',
    accept: 'Accept',
    leave: 'Leave',
    declineDelegation: 'Decline delegation',
    leaveDelegation: 'Leave delegation',
    youWillLoseAccessToThis: 'You will lose access to this account. You can be invited again later.',
    handTheManagementOfYourPosts: 'Hand the management of your posts and ads to a person',
    withA: ', with a',
    veryGranular: 'very granular',
    permissionEGPublishOnFacebook: 'permission (e.g. publish on Facebook but not on LinkedIn). They keep their own account and only see what you grant. Separate from',
    roles: 'Roles',
    internalMembersAndThe: '(internal members) and the',
    team: 'Team',
    inviteAnExternalManager: 'Invite an external manager',
    restrictedAccess: 'Restricted access',
    onlyTheOwnerAndAdministratorsCan: 'Only the owner and administrators can grant a delegation.',
    grantedDelegations: 'Granted delegations',
    loadingDelegations: 'Loading delegations…',
    noDelegationGranted: 'No delegation granted',
    inviteAnExternalManagerAboveTo: 'Invite an external manager above to give them granular access.',
    receivedDelegations: 'Received delegations',
    pending2: 'pending',
    noDelegationReceived: 'No delegation received',
    whenAClientGivesYouAccess: 'When a client gives you access to their account, the invitation appears here.',
    active: 'Active',
  },
  developerApiApi: {
    delivered: 'delivered',
    retryScheduled: 'retry scheduled',
    retryInProgress: 'retry in progress',
    permanentFailure: 'permanent failure',
    failed: 'failed',
    content: 'Content',
    publishing: 'Publishing',
    emailOutreach: 'Email / outreach',
    socialInbox: 'Social inbox',
    automations: 'Automations',
  },
  developerApiPage: {
    issuerLocal: 'local',
    triggerReplay: 'replay',
    triggerTest: 'test',
    couldNotLoadTheApplications: 'Could not load the applications.',
    labelCopiedToTheClipboard: '{{label}} copied to the clipboard.',
    couldNotCopySelectAndCopy: 'Could not copy — select and copy it manually.',
    couldNotRenewTheSecret: 'Could not renew the secret.',
    applicationNameRevoked: 'Application “{{name}}” revoked.',
    couldNotRevoke: 'Could not revoke.',
    developerApi: 'Developer API',
    oauth2ApplicationsSignedWebhooksWithRetr: 'OAuth2 applications, signed webhooks with retries, event catalog, OpenAPI reference, remote MCP server and Zapier / Make apps.',
    refresh: 'Refresh',
    registeringApplicationsAndManagingTheirS: 'Registering applications and managing their secrets is reserved for the account owner or administrator (Owner/Admin only).',
    yourApplications: 'Your applications',
    loadingApplications: 'Loading applications…',
    noApplications: 'No applications',
    registerAFirstApplicationAboveTo: 'Register a first application above to get a client_id and a client_secret.',
    renewTheSecret: 'Renew the secret?',
    renew: 'Renew',
    aNewSecretWillBeGenerated: 'A new secret will be generated for “',
    theOldSecretWillStopWorking: '”. The old secret will stop working',
    immediately: 'immediately',
    updateYourIntegrations: '— update your integrations.',
    revokeTheApplication: 'Revoke the application?',
    revoke: 'Revoke',
    doYouReallyWantToRevoke: 'Do you really want to revoke “',
    itsTokensCanNoLongerBe: '”? Its tokens can no longer be issued and this action is permanent.',
    applications: 'Applications',
    webhooks: 'Webhooks',
    deadLetters: 'Dead letters',
    events: 'Events',
    apiReference: 'API reference',
    openapiSchema: 'OpenAPI schema',
    remoteMcp: 'Remote MCP',
    zapierMake: 'Zapier / Make',
    developerPortalSections: 'Developer portal sections',
    clientCredentialsTokensAreIssuedBy: 'Client-credentials tokens are issued by Keycloak and accepted by the whole API.',
    keycloakTokensAcceptedAcrossThePlatform: 'Keycloak tokens — accepted across the platform',
    withoutAKeycloakAdminCredentialTokens: 'Without a Keycloak admin credential, tokens are only honored by the integrations service (webhooks, events).',
    localTokensEnableKeycloakIssuanceKeycloa: 'Local tokens — enable Keycloak issuance (KEYCLOAK_ADMIN_*)',
    theScopeListCouldNotBe: 'The scope list could not be loaded. You can register the application without scopes and add them later via “Edit”.',
    applicationName: 'Application name *',
    descriptionOptional: 'Description (optional)',
    theNameMustBeAtLeast: 'The name must be at least 2 characters long.',
    applicationNameRegistered: 'Application “{{name}}” registered.',
    couldNotRegisterTheApplication: 'Could not register the application.',
    registerAnApplication: 'Register an application',
    the: 'The',
    clientSecret: 'client_secret',
    willOnlyBeShownOnceOauth2: 'will only be shown once. OAuth2 client-credentials flow — no redirect URI is required.',
    n2To160Characters: '2 to 160 characters.',
    eGInternalCrmIntegration: 'E.g. Internal CRM integration',
    whatThisApplicationIsFor: 'What this application is for.',
    nightlyContactSync: 'Nightly contact sync…',
    scopesWhatTheApplicationIsAllowed: 'Scopes — what the application is allowed to do',
    scope: 'scope',
    selected: 'selected',
    saving: 'Saving…',
    registerApplication: 'Register application',
    applicationUpdated: 'Application updated.',
    couldNotUpdate: 'Could not update.',
    editApplication: 'Edit application',
    cancel: 'Cancel',
    save: 'Save',
    name: 'Name *',
    description: 'Description',
    scopes: 'Scopes',
    tokensAlreadyIssuedKeepTheirScopes: 'Tokens already issued keep their scopes until they expire; new tokens reflect the new list.',
    active: 'active',
    authorityThatSignsThisApplicationS: 'Authority that signs this application\'s tokens',
    copyTheClientId: 'Copy the client_id',
    copy: 'Copy',
    noScopes: 'No scopes',
    createdOn: 'Created on',
    lastUsed: 'Last used',
    edit: 'Edit',
    renewSecret: 'Renew secret',
    revokeName: 'Revoke {{name}}',
    applicationRegistered: 'Application registered',
    newSecretGenerated: 'New secret generated',
    iCopiedTheSecretClose: 'I copied the secret — Close',
    copyTheSecretNow: 'Copy the secret now.',
    forSecurityReasonsItIsHashed: 'For security reasons, it is hashed at rest and will',
    neverBeShownAgain: 'never be shown again',
    ifYouLoseItRenewIt: '. If you lose it, renew it.',
    useTheseCredentialsWithTheOauth2: 'Use these credentials with the OAuth2 grant',
    clientCredentials: 'client_credentials',
    toObtainAnAccessTokenFor: 'to obtain an access token for the public API.',
    loadingTheApiReference: 'Loading the API reference…',
    apiReferenceUnavailable: 'API reference unavailable',
    thePublicApiDescriptionDocumentCould: 'The public API description document could not be loaded. Try again with “Refresh”.',
    publicApiReference: 'Public API reference',
    versionVersion: '· version {{version}}',
    baseUrl: 'Base URL',
    tokenEndpoint: 'Token endpoint',
    tokenIntrospection: 'Token introspection',
    openapiDocumentV1: 'OpenAPI document (v1)',
    resourcesLength: 'Resources ({{length}})',
    resource: 'Resource',
    path: 'Path',
    methods: 'Methods',
    availableScopesLength: 'Available scopes ({{length}})',
    subscriptionManagement: 'Subscription management',
    signatureHeader: 'Signature header',
    signatureScheme: 'Signature scheme',
    eventsLength: 'Events ({{length}})',
    copyLabel: 'Copy {{label}}',
    allEvents: 'All events',
    couldNotLoadTheWebhookSubscriptions: 'Could not load the webhook subscriptions.',
    webhookSubscriptionDeleted: 'Webhook subscription deleted.',
    couldNotDelete: 'Could not delete.',
    receivePlatformEventsOnYourOwn: 'Receive platform events on your own URLs — each delivery is signed (HMAC SHA-256, header',
    xPubsocialnetSignature: 'X-PubSocialNet-Signature',
    automaticallyRetriedAndLoggedAttemptBy: '), automatically retried and logged attempt by attempt.',
    newSubscription: 'New subscription',
    ownerAdminOnlyOwnerAdminOnly: 'Owner / admin only (Owner/Admin only)',
    deliveries: 'Deliveries',
    delivered: 'Delivered',
    scheduledRetries: 'Scheduled retries',
    successRate: 'Success rate',
    retryPolicyUpTo: 'Retry policy: up to',
    maxAttemptsAttempts: '{{max_attempts}} attempts',
    onTransportErrors408425429: ') on transport errors, 408/425/429 or 5xx; any other 4xx goes straight to dead letter. An endpoint is disabled after {{auto_disable_after_consecutive_dead_letters}}',
    consecutivePermanentFailuresSecretRotati: 'consecutive permanent failures. Secret rotation: the old secret co-signs for',
    loadingWebhookSubscriptions: 'Loading webhook subscriptions…',
    noWebhookSubscriptions: 'No webhook subscriptions',
    createASubscriptionToReceiveEvents: 'Create a subscription to receive events (contact created, stage change, publication, email reply, form submitted…) on your endpoint.',
    noSubscriptionHasBeenCreatedFor: 'No subscription has been created for your organization yet.',
    deleteTheSubscription: 'Delete the subscription?',
    delete: 'Delete',
    theEndpoint: 'The endpoint',
    willNoLongerReceiveAnyEvents: 'will no longer receive any events. This action is permanent.',
    reservedForTheOwnerOrAdministrator: 'Reserved for the owner or administrator (Owner/Admin only).',
    couldNotReadTheSecret: 'Could not read the secret.',
    signingSecret: 'signing secret',
    testDeliveredV: 'Test delivered{{v}}.',
    testFailedCodeReason: 'Test failed{{code}}{{reason}}',
    couldNotSendTheTest: 'Could not send the test.',
    couldNotRotateTheSecret: 'Could not rotate the secret.',
    active2: 'active',
    inactive: 'inactive',
    oldSecretStillAccepted: 'Old secret still accepted',
    rotationInProgress: 'rotation in progress',
    automaticallyDisabledDisabledReasonFixTh: 'Automatically disabled: {{disabled_reason}}. Fix the endpoint, then re-enable it via “Edit”.',
    noEvents: 'No events',
    signingSecret2: 'Signing secret',
    hideTheSecret: 'Hide the secret',
    revealTheSecret: 'Reveal the secret',
    hide: 'Hide',
    reveal: 'Reveal',
    copyTheSigningSecret: 'Copy the signing secret',
    renewTheSigningSecret: 'Renew the signing secret',
    createdOn2: 'Created on',
    lastSuccess: 'Last success',
    lastFailure: 'Last failure',
    consecutivePermanentFailures: 'Consecutive permanent failures',
    maxMaxAttemptsAttempts: ' · max {{max_attempts}} attempts',
    deliveryLog: 'Delivery log',
    sending: 'Sending…',
    sendATest: 'Send a test',
    deleteUrl: 'Delete {{url}}',
    renewTheSigningSecret2: 'Renew the signing secret?',
    aNewSecretWillBeGenerated2: 'A new secret will be generated. The old secret keeps co-signing every delivery during the grace period (two values',
    inTheHeaderSoYourReceiver: 'in the header) so your receiver switches over without losing any event.',
    signingSecretRenewed: 'Signing secret renewed',
    close: 'Close',
    theOldSecretRemainsAcceptedUntil: 'The old secret remains accepted until',
    vDeployTheNewSecretBefore: '({{v}}). Deploy the new secret before this deadline.',
    newSecret: 'new secret',
    oldSecretGrace: 'old secret (grace)',
    allAttempts: 'All attempts',
    retryScheduled: 'Retry scheduled',
    permanentFailureDeadLetter: 'Permanent failure (dead letter)',
    failedSuperseded: 'Failed / superseded',
    retry: 'retry',
    viewTheSentBody: 'View the sent body',
    body: 'Body',
    replayThisDelivery: 'Replay this delivery',
    replay: 'Replay',
    deliveryId: 'Delivery {{id}}',
    event: 'Event',
    eventId: 'Event ID',
    attempt: 'Attempt',
    signatureSent: 'Signature sent',
    requestBodySignedJson: 'Request body (signed JSON)',
    responseError: 'Response / error',
    couldNotLoadTheLog: 'Could not load the log.',
    labelCopied: '{{label}} copied.',
    couldNotCopy: 'Could not copy.',
    replayDeliveredV: 'Replay delivered{{v}}.',
    replayVV2V3: 'Replay {{v}}{{v2}}{{v3}}',
    couldNotReplay: 'Could not replay.',
    couldNotLoadTheDelivery: 'Could not load the delivery.',
    filter: 'Filter',
    refreshTheLog: 'Refresh the log',
    refresh2: 'Refresh',
    delivered2: 'delivered',
    retryScheduledRetrying: '· {{retry_scheduled}} retrying ·',
    dead: 'dead',
    letter: 'letter',
    successV: '· success {{v}} %',
    nextAttemptV: ' · next attempt {{v}}',
    loadingTheLog: 'Loading the log…',
    noDeliveries: 'No deliveries',
    forThisFilter: ' for this filter',
    yet: ' yet',
    useSendATestToCheck: '. Use “Send a test” to check your endpoint.',
    date: 'Date',
    status: 'Status',
    nextAttempt: 'Next attempt',
    duration: 'Duration',
    detail: 'Detail',
    actions: 'Actions',
    attempt2: 'attempt',
    previous: 'Previous',
    next: 'Next',
    couldNotLoadTheDeadLetters: 'Could not load the dead letters.',
    replayDeliveredTheDeadLetterIs: 'Replay delivered — the dead letter is resolved.',
    replayVV2: 'Replay {{v}}{{v2}}',
    deliveriesThatExhaustedTheirAttemptsOr: 'Deliveries that exhausted their attempts (or were rejected by a non-retryable 4xx), across all endpoints. Replay them once your receiver is fixed.',
    loading: 'Loading…',
    noDeadLetters: 'No dead letters',
    allDeliveriesSucceededOrAreBeing: 'All deliveries succeeded or are being retried.',
    endpoint: 'Endpoint',
    attempts: 'Attempts',
    replaying: 'Replaying…',
    totalDeadLetterS: '{{total}} dead letter(s)',
    couldNotLoadTheEventCatalog: 'Could not load the event catalog.',
    couldNotLoadTheExample: 'Could not load the example.',
    eventCatalogLength: 'Event catalog ({{length}})',
    everyKeyCanBeSubscribedTo: 'Every key can be subscribed to via webhook, triggers a Zap / Make scenario and appears in the OpenAPI document. “Pending” = the consumer is ready but the emitting service does not publish the event yet.',
    filterByCategory: 'Filter by category',
    allCategories: 'All categories',
    loadingTheCatalog: 'Loading the catalog…',
    key: 'Key',
    category: 'Category',
    availability: 'Availability',
    example: 'Example',
    copyKey: 'Copy {{key}}',
    derivedFromDerivedFrom: 'Derived from {{derived_from}}.',
    available: 'available',
    pending: 'pending',
    view: 'View',
    exampleKey: 'Example — {{key}}',
    copyJson: 'Copy JSON',
    scopes2: 'Scopes:',
    public: 'public',
    parameters: 'Parameters',
    required: ', required',
    requestBody: 'Request body:',
    schema: 'Schema',
    responses: 'Responses',
    couldNotLoadTheOpenapiDocument: 'Could not load the OpenAPI document.',
    other: 'Other',
    publicVersionedOpenapi31Document: 'Public, versioned OpenAPI 3.1 document: {{length}} operations, {{schemaCount}} schemas,',
    webhookEventsImportItIntoPostman: 'webhook events. Import it into Postman, Insomnia or an SDK generator.',
    apiVersion: 'API version',
    openTheJson: 'Open the JSON',
    copyTheUrl: 'Copy the URL',
    loadingTheSchema: 'Loading the schema…',
    filterByTag: 'Filter by tag',
    allTagsLength: 'All tags ({{length}})',
    searchAPathASummaryAn: 'Search a path, a summary, an operationId…',
    searchAnOperation: 'Search an operation',
    noOperationMatches: 'No operation matches.',
    documentedWebhookEventsWebhookcountSigne: 'Documented webhook events ({{webhookCount}}) — signed envelope',
    theRemoteMcpServerIsUnreachable: 'The remote MCP server is unreachable right now.',
    remoteMcpServerClaudeChatgptAgents: 'Remote MCP server (Claude, ChatGPT, agents)',
    exposeTheCrmToolsContactsCompanies: 'Expose the CRM tools (contacts, companies, groups, deals, notes, tasks, interactions) to an external AI assistant through the Model Context Protocol. OAuth 2.1 authentication (code + PKCE): the assistant discovers the authorization server on its own.',
    mcpServerUrlStreamableHttp: 'MCP server URL (Streamable HTTP)',
    queryingTheMcpServer: 'Querying the MCP server…',
    sseTransportLegacyHosts: 'SSE transport (legacy hosts)',
    protectedResourceMetadataRfc9728: 'Protected resource metadata (RFC 9728)',
    authorizationServer: 'Authorization server',
    dynamicClientRegistration: 'Dynamic client registration',
    server: 'Server',
    protocols: 'Protocols',
    exposedTools: 'Exposed tools',
    activeSessions: 'Active sessions',
    toolsCount: 'Tools ({{count}})',
    gettingStarted: 'Getting started',
    claudeClaudeAiSettingsConnectorsAdd: 'Claude (claude.ai → Settings → Connectors → “Add custom connector”) or ChatGPT (Connectors): paste the MCP server URL above.',
    theAssistantReceivesA401With: 'The assistant receives a 401 with',
    resourceMetadata: 'resource_metadata',
    readsTheRfc9728DocumentRegisters: ', reads the RFC 9728 document, registers with the authorization server (',
    proxyToKeycloak: 'proxy to Keycloak',
    thenStartsTheOauthSignIn: ') then starts the OAuth sign-in (PKCE {{pkce}}).',
    signInWithYourKarexiaAccount: 'Sign in with your Karexia account: the tools act in YOUR tenant, with your roles.',
    writeToolsFirstReturnAPreview: 'Write tools first return a preview; the assistant must call the tool again with',
    confirmTrue: 'confirm=true',
    afterYourApproval: 'after your approval.',
    dynamicRegistrationIsNotConfiguredUse: 'Dynamic registration is not configured: use a client pre-registered in Keycloak (see the integration documentation).',
    zapierAndMakeApps: 'Zapier and Make apps',
    twoReadyToPublishAppsGenerated: 'Two ready-to-publish apps, generated from the event catalog: every event is an instant trigger (signed REST hook), and the actions cover creating / updating a contact, adding a note, creating a task, creating a company or a deal, adding to a group and enrolling in a sequence.',
    connectorManifestTriggersActionsRestHook: 'Connector manifest (triggers, actions, REST hooks)',
    registerAnApplicationAboveWithThe: 'Register an application above with the required scopes.',
    inZapierConnectPubsocialnetWithThe: 'In Zapier, connect “PubSocialNet” with the base URL, the',
    clientId: 'client_id',
    andThe: 'and the',
    chooseATriggerEGNew: 'Choose a trigger (e.g. “New Contact”): Zapier subscribes to the webhook automatically.',
    servicesPythonIntegrationsServiceConnect: 'services-python/integrations-service/connectors/zapier',
    makeIntegromat: 'Make (Integromat)',
    importTheCustomAppBaseConnection: 'Import the custom app (base, connection, webhook, modules).',
    createThePubsocialnetClientCredentialsCo: 'Create the “PubSocialNet (client credentials)” connection with your credentials.',
    theWatchEventsInstantModuleLists: 'The “Watch Events (instant)” module lists the events in the catalog.',
    servicesPythonIntegrationsServiceConnect2: 'services-python/integrations-service/connectors/make',
    publishingToThePublicZapierMake: 'Publishing to the public Zapier / Make directories is done from the owner\'s developer account (publisher review) — the apps are usable privately as soon as they are imported.',
    invalidUrlItMustStartWith: 'Invalid URL — it must start with https:// (or http://).',
    selectAtLeastOneEventOr: 'Select at least one event (or “All events”).',
    theSecretMustBeBetween8: 'The secret must be between 8 and 255 characters.',
    theMaximumNumberOfAttemptsMust: 'The maximum number of attempts must be between 1 and 20.',
    webhookSubscriptionCreated: 'Webhook subscription created.',
    webhookSubscriptionUpdated: 'Webhook subscription updated.',
    couldNotCreateTheSubscription: 'Could not create the subscription.',
    couldNotUpdateTheSubscription: 'Could not update the subscription.',
    newWebhookSubscription: 'New webhook subscription',
    editWebhookSubscription: 'Edit webhook subscription',
    createSubscription: 'Create subscription',
    destinationUrl: 'Destination URL *',
    httpsRecommendedTheEndpointThatWill: 'HTTPS recommended — the endpoint that will receive the signed events.',
    httpsExampleComWebhooksPubsocialnet: 'https://example.com/webhooks/pubsocialnet',
    whatThisSubscriptionIsFor: 'What this subscription is for.',
    syncingContactsToOurInternalCrm: 'Syncing contacts to our internal CRM…',
    maximumAttemptsOptional: 'Maximum attempts (optional)',
    n1To20EmptyDefaultPolicy: '1 to 20 — empty = default policy.',
    events2: 'Events *',
    allEvents2: 'All events (',
    subscribeToEveryPlatformEventCurrent: 'Subscribe to every platform event, current and future.',
    filterEvents: 'Filter events…',
    filterEvents2: 'Filter events',
    noEventMatchesSelectAllEvents: 'No event matches. Select “All events” to subscribe to everything.',
    event2: 'event',
    signingSecretOptional: 'Signing secret (optional)',
    n8To255CharactersLeaveEmpty: '8 to 255 characters. Leave empty to generate a secret automatically.',
    whsec: 'whsec_…',
    activeEventsAreDeliveredAsSoon: 'Active — events are delivered as soon as they occur.',
    webhookSubscriptionCreated2: 'Webhook subscription created',
    copyTheSigningSecretNow: 'Copy the signing secret now.',
    useItToVerifyTheHeader: 'Use it to verify the header',
    tTsV1HmacSha256Secret: 't=<ts>,v1=HMAC-SHA256(secret, ts + \'.\' + body)',
    ofEveryDeliveryYouCanFind: ') of every delivery. You can find it again later on the subscription page.',
    destinationUrl2: 'Destination URL',
    requestBody2: 'request body',
    key2: 'key',
  },
  governancePage: {
    agentsPersonas: 'Agents & personas',
    skills: 'Skills',
    prompts: 'Prompts',
    tools: 'Tools',
    everyone: 'Everyone',
    company: 'Company',
    client: 'Client',
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    somethingWentWrongPleaseTryAgain: 'Something went wrong. Please try again.',
    enterACompanyIdentifierTenantId: 'Enter a company identifier (tenant_id).',
    chooseAClient: 'Choose a client.',
    companyV: 'Company {{v}}',
    clientV: 'Client {{v}}',
    restrictedAccess: 'Restricted access',
    aiCapabilityGovernanceIsReservedFor: 'AI capability governance is reserved for platform administrators.',
    decideWhoCanUseEachAi: 'Decide who can use each AI capability: open to',
    everyone2: 'everyone',
    orReservedForASpecific: ', or reserved for a specific',
    company2: 'company',
    orASpecific: 'or a specific',
    client2: 'client',
    text: '.',
    capabilityType: 'Capability type',
    chooseASingular: 'Choose a {{singular}}',
    loadingCapabilities: 'Loading capabilities…',
    noSingularInTheRegistry: 'No {{singular}} in the registry',
    firstAddAnEntryInThe: 'First add an entry in the AI Registry.',
    search: 'Search',
    filterTheLabel: 'Filter the {{label}}…',
    filterTheLabel2: 'Filter the {{label}}',
    select: '— Select —',
    companies: 'Companies',
    clients: 'Clients',
    availabilityOf: 'Availability of',
    loadingAccess: 'Loading access…',
    noAccessGrantedThisCapabilityIs: 'No restriction — this capability is available to everyone. The first grant will reserve it to its audience (everyone else loses access).',
    grantedAccess: 'Granted access',
    revokeAccessV: 'Revoke access “{{v}}”',
    makeAvailableTo: 'Make available to…',
    audience: 'Audience',
    companyIdentifierTenantId: 'Company identifier (tenant_id) *',
    pasteTheTenantIdOfThe: 'Paste the tenant_id of the company to authorize (no directory is available here).',
    eG3f9aTenantId: 'e.g. 3f9a…-tenant-id',
    clientOrganizationMember: 'Client (organization member) *',
    noActiveMemberToTargetFirst: 'No active member to target — first invite a member in the Team tab.',
    chooseAClient2: 'Choose a client',
    selectAClient: '— Select a client —',
    assigning: 'Assigning…',
    makeAvailable: 'Make available',
    willBeAccessibleToAllTenants: 'Will be accessible to all tenants and clients.',
    reservedForTheSpecifiedCompany: 'Reserved for the specified company.',
    reservedForTheSelectedClient: 'Reserved for the selected client.',
  },
  integrationsHubPage: {
    socialAccounts: 'Social accounts',
    connectAndManageYourSocialMedia: 'Connect and manage your social media accounts (LinkedIn, Facebook, Instagram, X, YouTube…).',
    connectorsCredentials: 'Connectors & credentials',
    configureApplicationConnectorsAndSecurel: 'Configure application connectors and securely store their keys and credentials.',
    aiToolsMcpByokKeys: 'AI tools (MCP) & BYOK keys',
    registryOfAgentsModelsPromptsAnd: 'Registry of agents, models, prompts and MCP tools, with your own API keys (BYOK).',
    connectorConnected: '{{connector}} connected.',
    connectorConnected2: 'Connector connected.',
    connectionFailedV: 'Connection failed{{v}}.',
    nameDisconnected: '{{name}} disconnected.',
    nameEnabled: '{{name}} enabled.',
    nameSetupRequiredApplicationName2Not: '{{name}}: setup required (application {{name2}} not configured by the administrator).',
    nameSetupRequired: '{{name}}: setup required.',
    nameConnectionValid: '{{name}}: connection valid.',
    testFailed: 'test failed.',
    advancedIntegrations: 'Advanced integrations',
    aSingleEntryPointToAll: 'A single entry point to all your external integrations: social accounts, connectors, AI tools and the marketplace.',
    integrationSurfaces: 'Integration surfaces',
    goStraightToEachConfigurationArea: 'Go straight to each configuration area.',
    openTitle: 'Open {{title}}',
    open: 'Open',
    marketplace: 'Marketplace',
    connectThirdPartyAppsZapierSlack: 'Connect third-party apps (Zapier, Slack, HubSpot…) to your workspace.',
    refresh: 'Refresh',
    loadingTheMarketplace: 'Loading the marketplace…',
    noIntegrationAvailable: 'No integration available',
    noThirdPartyAppIsOffered: 'No third-party app is offered yet. Check back later.',
    aiMedia: 'AI & Media',
    connectYourOwnMediaGenerationAccounts: 'Connect your own media-generation accounts (ElevenLabs, HeyGen, Runway or a custom provider). Billing and quotas stay on your provider account; otherwise the platform uses its own key.',
    aiMediaProviderKeys: 'AI & Media (provider keys)',
    loadingAiProviders: 'Loading AI providers…',
    connected: 'Connected',
    setupRequired: 'Setup required',
    testing: 'Testing…',
    test: 'Test',
    disconnect: 'Disconnect',
    thisIntegrationMustBeConfiguredBy: 'This integration must be configured by the administrator.',
    connect: 'Connect',
    theApiKeyIsRequired: 'The API key is required.',
    theTestUrlIsRequiredFor: 'The test URL is required for a custom provider.',
    nameConnected: '{{name}} connected.',
    connectName: 'Connect {{name}}',
    cancel: 'Cancel',
    connecting: 'Connecting…',
    enterTheApiKeyIssuedBy: 'Enter the API key issued by your {{name}} account. It is stored encrypted and never shown again.',
    apiKey: 'API key',
    issuedFromYourProviderAccount: 'Issued from your provider account.',
    accountLabelOptional: 'Account label (optional)',
    eGMainAccount: 'E.g. “Main account”.',
    mainAccount: 'Main account',
    baseUrlOptional: 'Base URL (optional)',
    theProviderSApiEntryPoint: 'The provider\'s API entry point, e.g. https://api.example.com/v1.',
    httpsApiExampleComV1: 'https://api.example.com/v1',
    authenticationHeader: 'Authentication header',
    eGAuthorizationOrXApi: 'E.g. “Authorization” or “x-api-key”.',
    authorization: 'Authorization',
    schemeOptional: 'Scheme (optional)',
    headerPrefixEGBearerLeave: 'Header prefix, e.g. “Bearer”. Leave empty if none.',
    bearer: 'Bearer',
    testUrl: 'Test URL',
    authenticatedGetEndpointUsedToValidate: 'Authenticated GET endpoint used to validate the key (“Test”).',
    httpsApiExampleComV1Me: 'https://api.example.com/v1/me',
  },
  mailboxesPage: {
    unchangedPlaceholder: 'Unchanged',
    readOnlyWriteRequired: 'Read only — write access required.',
    errorStatus: 'Error {{status}}',
    accessDeniedWritePermissionsRequired: 'Access denied — write permissions required.',
    planLimitReachedForSmtpMailboxes: 'Plan limit reached for SMTP mailboxes — upgrade to a higher plan.',
    couldNotLoadTheMailboxes: 'Could not load the mailboxes.',
    emailAddressAndSmtpHostRequired: 'Email address and SMTP host required.',
    credentialsUpdatedMailboxToReVerify: 'Credentials updated — mailbox to re-verify.',
    smtpMailboxConnected: 'SMTP mailbox connected.',
    connectingTheMailboxFailed: 'Connecting the mailbox failed.',
    connectionVerified: 'Connection verified.',
    failedError: 'Failed: {{error}}',
    connectionNotVerified: 'Connection not verified.',
    theConnectionTestFailed: 'The connection test failed.',
    disconnectThisMailbox: 'Disconnect this mailbox?',
    emailAddressWillBeDisconnectedAnd: '{{email_address}} will be disconnected (and its linked sending identity deleted).',
    disconnect: 'Disconnect',
    mailboxDisconnected: 'Mailbox disconnected.',
    disconnectingFailed: 'Disconnecting failed.',
    smtpSendingMailboxes: 'SMTP sending mailboxes',
    credentialsHostUserPasswordAreEncrypted: 'Credentials (host / user / password) are encrypted at rest and never shown again. To change them, re-enter them via “Edit credentials”.',
    credentialRotationTheNewCredentialsWill: 'Credential rotation — the new credentials will replace the previous ones.',
    emailAddressFrom: 'Email address (from)',
    salesAcmeCom: 'sales@acme.com',
    displayNameOptional: 'Display name (optional)',
    salesTeam: 'Sales team',
    smtpHost: 'SMTP host',
    smtpAcmeCom: 'smtp.acme.com',
    port: 'Port',
    user: 'User',
    password: 'Password',
    writeOnlyNeverShownAgain: 'Write-only — never shown again',
    dailyCap: 'Daily cap',
    implicitTls465: 'Implicit TLS (465)',
    starttls587: 'STARTTLS (587)',
    saving: 'Saving…',
    saveCredentials: 'Save credentials',
    connectMailbox: 'Connect mailbox',
    cancel: 'Cancel',
    noMailboxConnectedAddAnSmtp: 'No mailbox connected. Add an SMTP mailbox to send your campaigns/sequences.',
    address: 'Address',
    provider: 'Provider',
    status: 'Status',
    actions: 'Actions',
    testing: 'Testing…',
    test: 'Test',
    editCredentials: 'Edit credentials',
    couldNotLoadTheOrders: 'Could not load the orders.',
    domainAndAtLeastOneMailbox: 'Domain and at least one mailbox prefix required.',
    domainOrderCreated: 'Domain order created.',
    buyingADomainRequiresAProvider: 'Buying a domain requires a provider (registrar) key on the service side, which is not configured.',
    theOrderFailed: 'The order failed.',
    cancelThisOrder: 'Cancel this order?',
    theOrderForDomainWillBe: 'The order for {{domain}} will be marked as cancelled.',
    cancelTheOrder: 'Cancel the order',
    orderCancelled: 'Order cancelled.',
    cancellingFailed: 'Cancelling failed.',
    domainMailboxPurchase: 'Domain & mailbox purchase',
    provisionADedicatedSendingDomainAnd: 'Provision a dedicated sending domain and its mailboxes through a licensed registrar. Without a provider key configured on the service side, the order honestly returns a 501 and nothing is charged.',
    providerNotConfigured: 'Provider not configured —',
    domain: 'Domain',
    acmeOutreachCom: 'acme-outreach.com',
    mailboxPrefixes: 'Mailbox prefixes',
    commaSeparated: 'Comma-separated',
    salesJohn: 'sales, john',
    ordering: 'Ordering…',
    buyTheDomain: 'Buy the domain',
    noDomainOrders: 'No domain orders.',
    mailboxes: 'Mailboxes',
    domainPurchase: 'Domain purchase',
    sendingMailboxesDomains: 'Sending mailboxes & domains',
    connectAnSmtpSendingMailboxWrite: 'Connect an SMTP sending mailbox (write-only credentials), test it, and provision a dedicated sending domain.',
  },
  modelsAdminPage: {
    thisModelHasNoEditableRecord: 'This model has no editable record yet — create it first in the AI Registry.',
    actionReservedForAdministratorsTheGlobal: 'Action reserved for administrators (the global margin is reserved for the platform super-administrator).',
    errorStatus: 'Error {{status}}',
    theOperationFailedTryAgain: 'The operation failed. Try again.',
    modelIdRemovedFromTheSelector: '“{{model_id}}” removed from the selector.',
    modelIdAddedToTheSelector: '“{{model_id}}” added to the selector.',
    theMultiplierMustBeAPositive: 'The multiplier must be a positive number.',
    multiplierForModelIdSaved: 'Multiplier for “{{model_id}}” saved.',
    theGrossMarginMustBeBetween: 'The gross margin must be between 0 and 95%.',
    marginForModelIdResetTo: 'Margin for “{{model_id}}” reset to the global default.',
    marginForModelIdSetTo: 'Margin for “{{model_id}}” set to {{value}}%.',
    theGlobalMarginMustBeBetween: 'The global margin must be between 0 and 95%.',
    globalGrossMarginSetToMargin: 'Global gross margin set to {{margin_percent}}%.',
    autoTasktypeV: 'Auto “{{taskType}}” → {{v}}.',
    autoTasktypeResetToTheDefault: 'Auto “{{taskType}}” reset to the default router.',
    administratorsOnly: 'Administrators only',
    aiModelConfigurationSelectorCreditCost: 'AI model configuration (selector, credit cost, Auto defaults) is reserved for workspace administrators.',
    loadingTemplates: 'Loading templates…',
    chooseTheModelsOfferedInThe: 'Choose the models offered in the model selector (in addition to “Auto”), adjust their credit cost and set the default model behind “Auto” for each task type. Models are never restricted by plan — they are billed in credits.',
    globalGrossMarginDefault: 'Global gross margin (default)',
    grossMarginAppliedToAnyModel: 'Gross margin applied to any model without a specific margin. Billing multiplier',
    k11Margin100: 'k = 1 / (1 − margin/100)',
    n010NoChange20: '— 0% ⇒ ×1.0 (no change), 20% ⇒ ×1.25, 50% ⇒ ×2.0. Clamped to [0, 95].',
    grossMargin: 'Gross margin (%)',
    defaultGlobalGrossMargin: 'Default global gross margin',
    multiplier: 'Multiplier',
    creditsBaseCredit: 'Credits / base credit',
    readOnlyReservedForThePlatform: 'Read-only — reserved for the platform super-administrator.',
    save: 'Save',
    autoDefaultModelPerTask: 'Auto — default model per task',
    autoPicksTheBestModelFor: '“Auto” picks the best model for each task type. Reassign below the model “Auto” points to for each task.',
    noTaskHasAnExplicitDefault: 'No task has an explicit default model — “Auto” follows the built-in router.',
    autoModelForTask: 'Auto model for {{task}}',
    defaultRouter: '— Default router —',
    newTaskType: 'New task type',
    eGTextGeneration: 'e.g. text_generation',
    model: 'Model',
    choose: '— Choose —',
    assign: 'Assign',
    registeredModels: 'Registered models',
    models: 'Models',
    inTheSelector: 'In the selector',
    noRegisteredModel: 'No registered model',
    addModelsInTheAiRegistry: 'Add models in the AI Registry to configure them here.',
    scope: 'Scope',
    selector: 'Selector',
    billedK: 'Billed (×k)',
    credits1kInOut: 'Credits / 1k (in · out)',
    workspace: 'Workspace',
    global: 'Global',
    offerModelIdInTheSelector: 'Offer {{model_id}} in the selector',
    default: 'default',
    grossMarginForModelId: 'Gross margin (%) for {{model_id}}',
    resetTheMarginOfModelId: 'Reset the margin of {{model_id}} to the global default',
    inheritsTheDefault: 'inherits the default',
    creditMultiplierForModelId: 'Credit multiplier for {{model_id}}',
  },
  organizationSettingsPage: {
    quotasFollowPlan: 'Quotas follow your plan — to raise them, change plan from the Subscription tab.',
    pending: 'Pending',
    deleted: 'Deleted',
    maxUsers: 'Max users',
    numberOfMembersWhoCanJoin: 'Number of members who can join the organization.',
    postsMonth: 'Posts / month',
    postsAllNetworksPerCalendarMonth: 'Posts (all networks) per calendar month.',
    scheduledPosts: 'Scheduled posts',
    postsQueuedAtTheSameTime: 'Posts queued at the same time.',
    socialAccounts: 'Social accounts',
    connectedSocialMediaAccounts: 'Connected social media accounts.',
    aiGenerationsMonth: 'AI generations / month',
    aiGenerationCallsPerCalendarMonth: 'AI generation calls per calendar month.',
    storageMb: 'Storage (MB)',
    totalMediaSpaceAllocatedInMegabytes: 'Total media space allocated, in megabytes.',
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    organizationNotFoundOrAccessNot: 'Organization not found, or access not authorized.',
    reservedForOrganizationAdministrators: 'Reserved for organization administrators.',
    sessionWithoutAnOrganizationSignIn: 'Session without an organization — sign in again.',
    couldNotLoadTheOrganization: 'Could not load the organization.',
    theOrganizationNameIsRequired: 'The organization name is required.',
    organizationUpdated: 'Organization updated.',
    updateFailed: 'Update failed.',
    loadingTheOrganization: 'Loading the organization…',
    organizationUnavailable: 'Organization unavailable',
    signInAgainOrContactAn: 'Sign in again, or contact an administrator if the problem persists.',
    noOrganization: 'No organization',
    noOrganizationIsLinkedToYour: 'No organization is linked to your account.',
    yourOrganizationSIdentityAndQuotas: 'Your organization\'s identity and quotas.',
    editTheNameDomainAndQuotas: 'Edit the name, domain and quotas below.',
    writeAccessReservedForOrganizationAdmini: 'Write access reserved for organization administrators.',
    identity: 'Identity',
    name: 'Name',
    identifierSlug: 'Identifier (slug)',
    domain: 'Domain',
    plan: 'Plan',
    status: 'Status',
    organizationName: 'Organization name *',
    theOrganizationSPrimaryDomainOptional: 'The organization\'s primary domain (optional).',
    exampleCom: 'example.com',
    notEditable: 'Not editable.',
    editableFromTheSubscriptionTab: 'Editable from the Subscription tab.',
    saving: 'Saving…',
    save: 'Save',
    discardChanges: 'Discard changes',
    noPendingChanges: 'No pending changes.',
    quotas: 'Quotas',
    active: 'Active',
    suspended: 'Suspended',
    free: 'Free',
    professional: 'Professional',
    company: 'Company',
  },
  referralPage: {
    labelCopied: '{{label}} copied.',
    couldNotCopy: 'Could not copy.',
    codeAcceptedRewardCreditsCreditsPaid: 'Code accepted — {{reward_credits}} credits paid to your referrer.',
    codeAccepted: 'Code accepted.',
    referrals: 'Referrals',
    creditsEarned: 'Credits earned',
    rewardReferral: 'Reward / referral',
    yourReferralCode: 'Your referral code',
    shareThisCodeForEveryPerson: 'Share this code: for every person who uses it, you receive',
    aiCreditsInYourWallet: 'AI credits in your wallet.',
    copyCode: 'Copy code',
    copyLink: 'Copy link',
    useAReferralCode: 'Use a referral code',
    ifAnAmbassadorInvitedYouEnter: 'If an ambassador invited you, enter their code (once only).',
    code: 'Code',
    abcd1234: 'ABCD1234',
    validating: 'Validating…',
    useCode: 'Use code',
    yourReferrals: 'Your referrals',
    noReferralsYet: 'No referrals yet.',
    rewardCreditsCrStatus: '+{{reward_credits}} cr · {{status}}',
    voucherAcceptedCreditsCreditsAdded: 'Voucher accepted — {{credits}} credits added.',
    voucherAccepted: 'Voucher accepted.',
    redeemAVoucher: 'Redeem a voucher',
    enterAPromoCodeToCredit: 'Enter a promo code to credit your AI wallet.',
    voucherCode: 'Voucher code',
    welcome50: 'WELCOME50',
    redeemVoucher: 'Redeem voucher',
    link: 'Link',
  },
  revenueSeoSection: {
    whsec: 'whsec_…',
  },
  reviewPolicy: {
    reviewRequired: 'Review required',
    themeTheme: 'theme {{theme}}',
    templateTemplate: 'template {{template}}',
    everythingCatchAll: 'Everything (catch-all)',
    couldNotLoadTheReviewPolicy: 'Could not load the review policy.',
    couldNotSaveTheSettingHas: 'Could not save the setting (has the Law 25 notice been accepted?).',
    deleteTheRuleName: 'Delete the rule “{{name}}”?',
    loadingThePolicy: 'Loading the policy…',
    autoPublishing: 'Auto-publishing',
    automatedDecisionNoticeLaw25: 'Automated decision notice (Law 25)',
    byDefaultEveryPostIsHeld: 'By default, every post is held for human approval. Enabling auto-publishing lets a rule publish without intervention when its confidence exceeds the threshold and the compliance check (CASL / Law 25) passes. This decision is automated, logged and reversible; you can turn it off at any time.',
    iHaveReadAndAcceptThis: 'I have read and accept this notice.',
    enableAutoPublishingForEligibleRules: 'Enable auto-publishing for eligible rules',
    masterSwitchWhenOffEveryAuto: 'Master switch — when off, every “auto” rule stays inert.',
    enableAutoPublishing: 'Enable auto-publishing',
    firstAcceptTheLaw25Notice: 'First accept the Law 25 notice above to enable it.',
    defaultDecisionNoRuleMatches: 'Default decision (no rule matches):',
    rules: 'Rules',
    addARule: '+ Add a rule',
    noRules: 'No rules',
    withoutARuleEveryPostFollows: 'Without a rule, every post follows the default decision (review required).',
    priority: 'Priority',
    ruleNameV: 'Rule {{name}} — {{v}}',
    delete: 'Delete',
    simulateADecision: 'Simulate a decision',
    newRule: 'New rule',
    couldNotCreateTheRule: 'Could not create the rule.',
    newReviewRule: 'New review rule',
    name: 'Name',
    eGAutoPublishLinkedinIf: 'E.g. Auto-publish LinkedIn if confidence ≥ 80%',
    priorityLowerFirst: 'Priority (lower = first)',
    decision: 'Decision',
    platformsCommaSeparatedEmptyAll: 'Platforms (comma-separated, empty = all)',
    linkedinFacebook: 'linkedin, facebook',
    source: 'Source',
    all: 'All',
    autonomy: 'Autonomy',
    minimumConfidenceThresholdMinconfidence: 'Minimum confidence threshold: {{minConfidence}}%',
    simulationUnavailableBackendNotDeployed: 'Simulation unavailable (backend not deployed?).',
    platform: 'Platform',
    confidenceConfidencepct: 'Confidence: {{confidencePct}}%',
    approvalExplicitlyRequiredNeverDowngrade: 'Approval explicitly required (never downgraded)',
    simulating: 'Simulating…',
    simulate: 'Simulate',
    decision2: 'Decision:',
    ruleMatchedRuleName: 'Rule: {{matched_rule_name}}',
    cancel: 'Cancel',
    saving: 'Saving…',
    createRule: 'Create rule',
    enabled: 'enabled',
    disabled: 'disabled',
    reservedToAccountOwnerOrAdmin: 'Reserved for the account owner or an administrator.',
    noticeAcceptedOn: 'Notice accepted on',
    versionSuffix: ' (version {{version}})',
    onlyOwnerOrAdminCanAcceptNotice: 'Only the owner or an administrator can accept this notice.',
    withdrawLoi25NoticeAcceptance: 'Withdraw acceptance of the Law 25 notice?',
    autoPublishingDisabledUntilNoticeAccepted: 'Auto-publishing will be disabled until the notice is accepted again.',
    withdrawAcceptance: 'Withdraw acceptance',
  },
  rolesPage: {
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    somethingWentWrongPleaseTryAgain: 'Something went wrong. Please try again.',
    hidePlatforms: 'Hide platforms',
    specificPlatforms: 'Specific platforms',
    allPlatforms: 'All platforms',
    permissionUnavailable: 'Permission unavailable.',
    sessionWithoutAnOrganizationSignIn: 'Session without an organization — sign in again.',
    enterARoleName: 'Enter a role name.',
    theRoleNameCannotBeEmpty: 'The role name cannot be empty.',
    restrictedAccess: 'Restricted access',
    managingRolesAndPermissionsIsReserved: 'Managing roles and permissions is reserved for administrators.',
    create: 'Create',
    customRoles: 'custom roles',
    andGiveThem: 'and give them',
    fineGrainedPermissions: 'fine-grained permissions',
    perResourceAndActionSeparateFrom: '(per resource and action). Separate from the',
    team: 'Team',
    tabWhichManagesGeneralOrganizationRoles: 'tab, which manages general organization roles (owner / admin / member).',
    loadingRolesAndPermissions: 'Loading roles and permissions…',
    roles: 'Roles',
    custom: 'Custom',
    system: 'System',
    permissions: 'Permissions',
    noCustomRoles: 'No custom roles',
    createYourFirstRoleBelow: 'Create your first role below.',
    roleList: 'Role list',
    permission: 'permission',
    newRole: 'New role',
    roleName: 'Role name *',
    eGContentEditor: 'e.g. Content editor',
    description: 'Description',
    optionalWhatThisRoleIsFor: 'Optional — what this role is for.',
    eGCanCreateAndEdit: 'e.g. Can create and edit content',
    creating: 'Creating…',
    createRole: 'Create role',
    selectARole: 'Select a role',
    chooseARoleOnTheLeft: 'Choose a role on the left to manage its name, description and permissions.',
    saving: 'Saving…',
    save: 'Save',
    cancel: 'Cancel',
    noDescription: 'No description.',
    systemRolesCannotBeEdited: 'System roles cannot be edited',
    edit: 'Edit',
    systemRolesCannotBeDeleted: 'System roles cannot be deleted',
    delete: 'Delete',
    noPermissionsInTheCatalog: 'No permissions in the catalog',
    thePermissionCatalogIsEmptyOn: 'The permission catalog is empty on the server.',
    members: 'Members',
    assignThisRoleToOrganizationMembers: 'Assign this role to organization members. A member\'s effective permission is the union of the roles assigned to them.',
    searchAMemberByNameOr: 'Search a member by name or email…',
    loadingMembers: 'Loading members…',
    noMembers: 'No members',
    noMemberHasJoinedThisOrganization: 'No member has joined this organization yet.',
    noResults: 'No results',
    noMemberMatchesYourSearch: 'No member matches your search.',
    organizationMembers: 'Organization members',
    deleteTheRole: 'Delete the role',
    deleteTheRole2: 'Delete the role “',
    thisActionCannotBeUndone: '”? This action cannot be undone.',
    deletePermanently: 'Delete permanently',
  },
  settingsLayout: {
    account: 'Account',
    organization: 'Organization',
    integrations: 'Integrations',
    ai: 'AI',
  },
  subscriptionPage: {
    unlimited: 'unlimited',
    paymentOverdue: 'Payment overdue',
    cancelled: 'Cancelled',
    active: 'Active',
    couldNotLoad: 'Could not load.',
    subscriptionUpdated: 'Subscription updated.',
    couldNotSubscribe: 'Could not subscribe.',
    cancelYourSubscriptionItWillStay: 'Cancel your subscription? It will stay active until the end of the current period.',
    subscriptionCancelledActiveUntilUntil: 'Subscription cancelled — active until {{until}}.',
    subscriptionCancelled: 'Subscription cancelled.',
    couldNotCancel: 'Could not cancel.',
    billingPortalUnavailable: 'Billing portal unavailable.',
    couldNotOpenThePortal: 'Could not open the portal.',
    yourPlan: 'Your plan:',
    cancellationScheduledYourSubscriptionSta: 'Cancellation scheduled — your subscription stays active until',
    theEndOfThePeriod: 'the end of the period',
    billingPlanChangesAndCancellation: ': billing, plan changes and cancellation.',
    manageBilling: 'Manage billing',
    cancelSubscription: 'Cancel subscription',
    aiCreditsMonth: 'AI credits (month)',
    creditBalance: 'Credit balance',
    projects: 'Projects',
    overage: 'Overage',
    aiCredits: 'AI credits',
    limitsContactsVSeatsV2Sequences: 'Limits: contacts {{v}} · seats {{v2}} · sequences {{v3}} · AI agents {{v4}}',
    current: 'current',
    free: 'Free',
    month: '/month',
    currencyNotConfiguredForThisPlan: 'Currency not configured for this plan — price in Canadian dollars.',
    billedInCurrency: '≈ billed in {{currency}}',
    aiCreditsMonthlyAiCreditsMonth: '✓ {{ai_credits_monthly}} AI credits / month',
    vContacts: '✓ {{v}} contacts',
    vSeats: '✓ {{v}} seats',
    vProjects: '✓ {{v}} projects',
    overageVCredit: 'Overage: {{v}} /credit',
    currentPlan: 'Current plan',
    choose: 'Choose',
    switchToThisPlan: 'Switch to this plan',
    couldNotLoadTheAddOns: 'Could not load the add-ons.',
    nameEnabledBilledByUsageOn: '{{name}} enabled — billed by usage, on top of your plan.',
    nameDisabled: '{{name}} disabled.',
    tooManyRequestsTryAgainIn: 'Too many requests — try again in a moment.',
    couldNotChangeTheAddOn: 'Could not change the add-on.',
    addOns: 'Add-ons',
    addOnsAreNotDeployedHere: 'Add-ons are not deployed here yet — no action required, the section will turn on by itself.',
    addOnsComingSoonNoAction: 'Add-ons coming soon — no action required',
    comingSoon: 'Coming soon',
    optionalUsageBilledFeatures: 'Optional, usage-billed features,',
    onTopOf: 'on top of',
    yourPlanYouOnlyPayFor: 'your plan. You only pay for what you use; nothing is charged while an add-on stays disabled.',
    addOnsIncludingApolloEnrichmentAre: 'Add-ons (including Apollo enrichment) are not deployed in this environment yet. This section will turn on automatically as soon as they go live.',
    noAddOnsOfferedYet: 'No add-ons offered yet.',
    enabled: 'Enabled',
    disabled: 'Disabled',
    billingUnitCreditsCreditSPer: 'Billing: {{unit_credits}} credit(s) per use, on top of your plan.',
    usageBasedBillingOnTopOf: 'Usage-based billing, on top of your plan.',
    disable: 'Disable',
    enable: 'Enable',
    usageForThePeriod: 'Usage for the period',
    noUsageInThisPeriodNo: 'No usage in this period — no estimated extra charge.',
    uses: 'Uses',
    creditsUsed: 'Credits used',
    estimatedCharge: 'Estimated charge',
    feature: 'Feature',
    credits: 'Credits',
    estimatedChargeTheFinalBilledAmount: 'Estimated charge — the final billed amount appears on your invoice.',
    unlimited2: 'Unlimited',
    selected: 'selected',
  },
  trustCenterPage: {
    nMonths: '{{n}} months',
    sentOn: 'Sent on {{date}}',
    expressConsent: 'Express consent',
    impliedConsent: 'Implied consent',
    legitimateInterest: 'Legitimate interest',
    noBasis: 'No basis',
    allowed: 'Allowed',
    blocked: 'Blocked',
    pending: 'Pending',
    underReview: 'Under review',
    resolved: 'Resolved',
    dismissed: 'Dismissed',
    high: 'High',
    caslCanadaAntiSpam: 'CASL (Canada anti-spam)',
    law25Quebec: 'Law 25 (Quebec)',
    gdprEuropeanUnion: 'GDPR (European Union)',
    caslGdprMixed: 'CASL + GDPR (mixed)',
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    accessDeniedThisActionIsReserved: 'Access denied: this action is reserved for account administrators / owners.',
    somethingWentWrongPleaseTryAgain: 'Something went wrong. Please try again.',
    impliedConsentWindowsMustBePositive: 'Implied-consent windows must be positive numbers of months.',
    complianceRegimeStatus: 'Compliance regime & status',
    edit: 'Edit',
    loadingTheConfiguration: 'Loading the configuration…',
    complianceServiceUnavailable: 'Compliance service unavailable',
    theRegimeConfigurationCouldNotBe: 'The regime configuration could not be loaded (service not deployed). The compliance engine stays fail-closed on sending.',
    applicableRegime: 'Applicable regime',
    select: '— Select —',
    windowBusinessRelationshipMonths: 'Window — business relationship (months)',
    durationOfImpliedConsentAfterThe: 'Duration of implied consent after the last transaction (CASL: 24 months).',
    windowInquiryMonths: 'Window — inquiry (months)',
    durationOfImpliedConsentAfterAn: 'Duration of implied consent after an inquiry (CASL: 6 months).',
    requireABalancingTestNoteFor: 'Require a balancing-test note for legitimate interest',
    gdprArt61FLegitimate: 'GDPR Art.6(1)(f): legitimate interest can only be used with a recorded balancing note.',
    provenancePrefixesRequiringAnArt14: 'Provenance prefixes requiring an Art.14 disclosure',
    commaSeparatedEGProviderScraper: 'Comma-separated — e.g. provider:, scraper:, import (indirect collection, GDPR Art.14).',
    providerScraper: 'provider:, scraper:',
    saving: 'Saving…',
    save: 'Save',
    cancel: 'Cancel',
    regime: 'Regime',
    businessRelationship: 'Business relationship',
    inquiry: 'Inquiry',
    legitimateInterestNote: 'Legitimate-interest note',
    sourceDisclosureGdprArt14Required: 'Source disclosure (GDPR Art.14) required for',
    noPrefixConfigured: 'No prefix configured.',
    consentRegisterAuditLog: 'Consent register (audit log)',
    loading: 'Loading…',
    refresh: 'Refresh',
    filterByEmail: 'Filter by email',
    contactExampleCom: 'contact@example.com',
    decision: 'Decision',
    all: 'All',
    filter: 'Filter',
    loadingTheRegister: 'Loading the register…',
    noDecisionRecorded: 'No decision recorded',
    noSendGateEvaluationMatchesThese: 'No send-gate evaluation matches these criteria for this tenant.',
    noDecisionRecorded2: 'No decision recorded.',
    unsubscribesDoNotContactDnc: 'Unsubscribes & Do-not-contact (DNC)',
    searchAContactByEmail: 'Search a contact by email',
    searching: 'Searching…',
    search: 'Search',
    noRecord: 'No record',
    thisContactHasNoLegalBasis: 'This contact has no legal basis recorded for this tenant — no consent, no revocation.',
    unsubscribedDnc: 'Unsubscribed / DNC',
    provenance: 'Provenance',
    legalBasis: 'Legal basis',
    consentMethod: 'Consent method',
    consentProof: 'Consent proof',
    consentRecordedOn: 'Consent recorded on',
    impliedConsentExpiresOn: 'Implied consent expires on',
    legitimateInterestNote2: 'Legitimate-interest note',
    revokedOn: 'Revoked on',
    reason: 'Reason',
    sourceDisclosureArt14: 'Source disclosure (Art.14)',
    revocationReason: 'Revocation reason',
    unsubscribed: 'unsubscribed',
    revokeUnsubscribe: 'Revoke / Unsubscribe',
    manualRevocationIsReservedForAdministrat: 'Manual revocation is reserved for administrators.',
    revokeConsent: 'Revoke consent',
    revokePermanently: 'Revoke permanently',
    theContact: 'The contact',
    willBeMarkedDoNotContact: 'will be marked “do not contact”. Any later send will be blocked by the send gate. This action is recorded in the audit log.',
    dataSubjectRequestsProofOfErasure: 'Data subject requests — proof of erasure',
    theErasureReportProvesServiceBy: 'The erasure report proves, service by service, that a tenant\'s data has been purged (right to be forgotten — Law 25 / GDPR). Erasure proofs only exist after the tenant is deleted (events',
    tenantPurged: 'tenant.purged',
    forAnActiveTenantEveryService: '); for an active tenant, every service shows as “pending”.',
    tenantIdentifier: 'Tenant identifier',
    yourOwnTenantByDefaultAn: 'Your own tenant by default — an administrator can check a deleted tenant.',
    generating: 'Generating…',
    generateTheReport: 'Generate the report',
    noReportAvailable: 'No report available',
    noErasureProofForThisTenant: 'No erasure proof for this tenant — expected while the tenant has not been deleted, or the tenant service is unavailable.',
    fullErasure: 'Full erasure',
    rowsErased: 'Rows erased',
    pendingServices: 'Pending services',
    servicesThatConfirmedTheErasure: 'Services that confirmed the erasure',
    noServiceHasConfirmedYet: 'No service has confirmed yet.',
    service: 'Service',
    rows: 'Rows',
    tables: 'Tables',
    confirmedOn: 'Confirmed on',
    noConfirmedService: 'No confirmed service.',
    complianceOverview: 'Compliance overview',
    loadingStatistics: 'Loading statistics…',
    statisticsUnavailable: 'Statistics unavailable',
    theComplianceServiceReturnedNoStatistics: 'The compliance service returned no statistics (service not deployed or no moderation activity in the period).',
    moderations: 'Moderations',
    approved: 'Approved',
    rejected: 'Rejected',
    averageToxicity: 'Average toxicity',
    reports: 'Reports',
    resolved2: 'Resolved',
    averageSentiment: 'Average sentiment',
    topReportTypes: 'Top report types',
    manage: 'Manage',
    contentReportsModerationQueue: 'Content reports (moderation queue)',
    filterByStatus: 'Filter by status',
    allStatuses: 'All statuses',
    contentReportsSpamHarassmentEtcSubmitted: 'Content reports (spam, harassment, etc.) submitted for this tenant. An administrator can handle them — review, resolve or dismiss — with a recorded resolution note.',
    handlingReportsIsReservedForAdministrato: 'Handling reports is reserved for administrators.',
    loadingReports: 'Loading reports…',
    noReports: 'No reports',
    noContentReportMatchesThisFilter: 'No content report matches this filter for this tenant.',
    noReports2: 'No reports.',
    newStatus: 'New status',
    resolutionNote: 'Resolution note',
    optionalRecordedWithTheReportReason: 'Optional — recorded with the report (reason for the decision).',
    eGContentRemovedAuthorWarned: 'e.g. Content removed; author warned.',
    tenantNotFound: 'Tenant not found',
    yourSessionCarriesNoTenantIdentifier: 'Your session carries no tenant identifier. Sign in again, then retry.',
    theTrustCenterMakesTheCompliance: 'The Trust Center makes the compliance engine (CASL · Law 25 · GDPR) visible and actionable: configure the regime, audit every send decision, manage unsubscribes and prove the right to be forgotten. The send gate stays fail-closed — a send is only allowed when a valid legal basis exists.',
    low: 'Low',
    critic: 'Critic',
    mandatory: 'Mandatory',
    evaluator: 'Evaluator',
    dueNotSentYet: 'Due — not sent yet',
    yes: 'Yes',
    no: 'No',
    last30Days: 'Last 30 days',
    content: 'Content',
    severity: 'Severity',
    status: 'Status',
  },
  useRoles: {
    reservedForTheAccountOwnerOr: 'Reserved for the account owner or administrator',
    readOnlyAccessReservedForMembers: 'Read-only access — reserved for members who can edit the account',
  },
  widgets: {
    gaugeReadout: 'Gauge: {{readout}}',
    gauge: 'Gauge',
    progress: 'Progress',
  },
  workspacesPage: {
    memberCountOne: '{{count}} member',
    memberCountMany: '{{count}} members',
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    theNameMustBeAtLeast: 'The name must be at least 2 characters long.',
    theLogoMustBeAnHttp: 'The logo must be an http(s) URL.',
    theColorMustBeInRrggbb: 'The color must be in #RRGGBB format.',
    workspaceUpdated: 'Workspace updated.',
    workspaceCreated: 'Workspace created.',
    editTheWorkspace: 'Edit the workspace',
    newWorkspace: 'New workspace',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    create: 'Create',
    brandClientName: 'Brand / client name',
    eGDupontBakery: 'E.g. Dupont Bakery',
    brandColor: 'Brand color',
    logoUrlOptional: 'Logo (URL, optional)',
    squareImageRecommendedHttps: 'Square image recommended (https://…).',
    https: 'https://…',
    memberAddedToTheWorkspace: 'Member added to the workspace.',
    accessRemoved: 'Access removed.',
    membersName: 'Members — {{name}}',
    noRoleAvailableFirstCreateA: 'No role available. First create a role in “Roles & permissions”.',
    member: 'Member',
    choose: 'Choose…',
    role: 'Role',
    add: 'Add',
    reservedForOwnersAndAdministrators: 'Reserved for owners and administrators',
    onlyAccountOwnersAndAdministratorsCan: 'Only account owners and administrators can see and manage a workspace\'s members.',
    noMemberAssigned: 'No member assigned',
    addedMembersCanOnlyAccessThis: 'Added members can only access this workspace.',
    remove: 'Remove',
    workspaceDeleted: 'Workspace deleted.',
    workspaces: 'Workspaces',
    oneWorkspacePerBrandOrClient: 'One workspace per brand or client: posts, media and connections stay separate, and each member only accesses the workspaces assigned to them.',
    newWorkspace2: '+ New workspace',
    workspacesNotEnabled: 'Workspaces not enabled',
    thisFeatureIsNotEnabledFor: 'This feature is not enabled for your account yet.',
    noWorkspaces: 'No workspaces',
    createOneWorkspacePerBrandOr: 'Create one workspace per brand or client to keep their content separate.',
    members: 'Members',
    edit: 'Edit',
    delete: 'Delete',
    deleteTheWorkspace: 'Delete the workspace?',
    nameWillBeDeletedAccessAssigned: '“{{name}}” will be deleted. Access assigned to this workspace will no longer grant anything; the account\'s content is not erased.',
  },
};

export default en;
